/* eslint-disable @typescript-eslint/explicit-function-return-type */
import {
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  EquisButtonBaseSizes,
  EquisButtonBaseTypes,
} from '@tn-equis/core/components/button';

import type { IButtonDownloadApp } from './button-download-app.types';
import { ButtonDownloadAppService } from './button-download-app.service';
import { SectionService } from '../../services/section.service';
import { mapStyle } from '../../utils/link';
import { PlatformService } from '../../services/platform.service';

@Component({
  selector: 'nx-button-download-app',
  templateUrl: './button-download-app.component.html',
  styleUrls: ['./button-download-app.component.scss'],
})
export class ButtonDownloadAppComponent
  implements IButtonDownloadApp, OnInit, OnDestroy
{
  constructor(
    private service: ButtonDownloadAppService,
    private section: SectionService,
    private platform: PlatformService,
  ) {}

  // REQUIRED INPUTS
  @Input() data!: IButtonDownloadApp['data'];
  // OPTIONAL INPUTS
  @Input() isFloating?: IButtonDownloadApp['isFloating'] = false;
  @Input() size: IButtonDownloadApp['size'] = EquisButtonBaseSizes.LARGE;
  @Input() width?: IButtonDownloadApp['width'] = 'full';

  // VIEW ELEMENTS
  @ViewChild('floatingDiv') floating!: ElementRef<HTMLDivElement>;

  // PROPERTIES
  /**
   * The threshold in percentage of the scroll height
   */
  private readonly SCROLL_THRESHOLD = 5;
  buttonType = EquisButtonBaseTypes.PRIMARY;
  get text() {
    return this.data.label;
  }

  /**
   * Gets the button style, based on the style input.
   */
  get buttonStyle() {
    return mapStyle(this.data.style);
  }

  get buttonWidth() {
    return this.width === 'full' ? '100%' : 'auto';
  }

  // METHODS
  /**
   * Shows the dialog on browsers and redirects to the app store on mobile
   */
  onClick() {
    if (this.platform.isMobile)
      this.service.redirectToApp(this.data.mobileLink);
    else this.service.show(this.data.dialogTitle);
  }

  // PRIVATE METHODS
  private handleScroll(event: Event) {
    const target = (event.target as Document).scrollingElement;
    const div = this.floating.nativeElement;
    if (target) {
      // Scroll values
      const scrollHeight = target.scrollHeight;
      const scrollTop = target.scrollTop;
      const clientHeight = target.clientHeight;
      const percentage = (scrollTop / (scrollHeight - clientHeight)) * 100;

      const passedThreshold = percentage < this.SCROLL_THRESHOLD;
      const isHidden = div.classList.contains('hide');

      if (passedThreshold && !isHidden) {
        this.floating.nativeElement.classList.remove('show');
        this.floating.nativeElement.classList.add('hide');
      } else if (!passedThreshold && isHidden) {
        this.floating.nativeElement.classList.add('show');
        this.floating.nativeElement.classList.remove('hide');
      }
    }
  }

  // HOOKS
  ngOnInit(): void {
    if (this.platform.isBrowser && this.isFloating)
      window.addEventListener('scroll', this.handleScroll.bind(this));

    // Store the data from contentful into the service
    this.service.dialogData = {
      formHint: this.data.formHint,
      qrHint: this.data.qrHint,
      qr: this.data.qr,
      dataLayer: this.data.dataLayer,
      section: this.section.sectionName ?? '',
    };
  }

  ngOnDestroy(): void {
    if (this.platform.isBrowser && this.isFloating) {
      window.removeEventListener('scroll', this.handleScroll.bind(this));
    }
  }
}
